import { useUser } from "@clerk/clerk-react";
import { useState } from "react";
import {
	ADMIN_NAV,
	isMairkoAdmin,
	type AdminNav,
} from "./admin.js";
import {
	AdminAuditPanel,
	AdminHomePanel,
	AdminKitPanel,
	AdminRosterPanel,
	AdminTenantsPanel,
} from "./AdminPanels.js";
import { useAppMode } from "./app-mode.js";
import { cn } from "./cn.js";
import { SignOutConfirm } from "./SignOutConfirm.js";
import {
	ArrowLeftIcon,
	Building2Icon,
	InboxIcon,
	LayoutDashboardIcon,
	LogOutIcon,
	ScrollTextIcon,
	ShieldIcon,
	UsersIcon,
} from "lucide-react";

function navIcon(id: AdminNav) {
	switch (id) {
		case "home":
			return LayoutDashboardIcon;
		case "roster":
			return UsersIcon;
		case "kit":
			return InboxIcon;
		case "tenants":
			return Building2Icon;
		case "audit":
			return ScrollTextIcon;
		default: {
			const _exhaustive: never = id;
			return _exhaustive;
		}
	}
}

function AdminPanel({ nav }: { nav: AdminNav }) {
	switch (nav) {
		case "home":
			return <AdminHomePanel />;
		case "roster":
			return <AdminRosterPanel />;
		case "kit":
			return <AdminKitPanel />;
		case "tenants":
			return <AdminTenantsPanel />;
		case "audit":
			return <AdminAuditPanel />;
		default: {
			const _exhaustive: never = nav;
			return _exhaustive;
		}
	}
}

/** Mairko firm console — operators only, not tenant workspace admin. */
export function AdminApp() {
	const { user } = useUser();
	const { enterMatrix } = useAppMode();
	const [nav, setNav] = useState<AdminNav>("home");
	const [signOutOpen, setSignOutOpen] = useState(false);

	const email =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses[0]?.emailAddress ??
		null;
	const allowed = isMairkoAdmin(email);
	const active = ADMIN_NAV.find((item) => item.id === nav) ?? ADMIN_NAV[0];
	const profileLabel = user?.fullName?.trim() || email || "Operator";
	const profileInitial = profileLabel.slice(0, 1).toUpperCase();

	if (!allowed) {
		return (
			<div className="app-frame">
				<div className="admin-denied">
					<ShieldIcon className="admin-denied-icon" aria-hidden />
					<h1 className="admin-denied-title">Admin only</h1>
					<p className="admin-denied-copy">
						This console is for Mairko operators. You’re signed in
						{email ? (
							<>
								{" "}
								as <code>{email}</code>
							</>
						) : null}
						, which isn’t on the allowlist.
					</p>
					<button
						type="button"
						className="btn btn-accent"
						onClick={enterMatrix}
					>
						Back to Matrix
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="app-frame">
			<div className="app app--sidebar-open admin-app">
				<aside className="sidebar admin-sidebar" aria-label="Mairko admin">
					<div className="sidebar-top">
						<div className="brand-lockup">
							<img
								className="brand-logo brand-logo--dark"
								src="/brand/mairko-logo-primary.svg"
								alt="Mairko"
								height={28}
							/>
							<img
								className="brand-logo brand-logo--light"
								src="/brand/mairko-logo-onlight.svg"
								alt="Mairko"
								height={28}
							/>
						</div>
						<p className="admin-brand-tag">
							<span className="admin-brand-tag-dot" aria-hidden />
							Firm admin
						</p>
					</div>

					<nav className="admin-nav" aria-label="Admin">
						{ADMIN_NAV.map((item) => {
							const Icon = navIcon(item.id);
							return (
								<button
									key={item.id}
									type="button"
									className={cn(
										"admin-nav-item",
										nav === item.id && "admin-nav-item--active",
									)}
									aria-current={nav === item.id ? "page" : undefined}
									onClick={() => setNav(item.id)}
								>
									<Icon className="size-4" aria-hidden />
									{item.label}
								</button>
							);
						})}
					</nav>

					<div className="sidebar-foot admin-sidebar-foot">
						<button
							type="button"
							className="admin-back-matrix"
							onClick={enterMatrix}
						>
							<ArrowLeftIcon className="size-4" aria-hidden />
							Back to Matrix
						</button>
						<div className="profile admin-profile">
							{user?.imageUrl ? (
								<img
									className="profile-avatar profile-avatar--img"
									src={user.imageUrl}
									alt=""
									width={32}
									height={32}
								/>
							) : (
								<div className="profile-avatar" aria-hidden>
									{profileInitial}
								</div>
							)}
							<div className="profile-copy">
								<p className="profile-name">{profileLabel}</p>
								<p className="profile-sub">Super admin</p>
							</div>
							<button
								type="button"
								className="admin-signout"
								onClick={() => setSignOutOpen(true)}
								aria-label="Sign out"
							>
								<LogOutIcon className="size-4" />
							</button>
						</div>
					</div>
				</aside>

				<main className="main admin-main">
					<header className="admin-header">
						<div>
							<p className="admin-eyebrow">Mairko admin</p>
							<h1 className="admin-title">{active?.label}</h1>
							<p className="admin-lede">{active?.lede}</p>
						</div>
					</header>
					<div className="admin-body">
						{active ? <AdminPanel nav={active.id} /> : null}
					</div>
				</main>
			</div>

			<SignOutConfirm
				open={signOutOpen}
				onOpenChange={setSignOutOpen}
			/>
		</div>
	);
}
